import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Box,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ContentPasteSearch as SearchIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useRole } from '../../hooks/useRole';

// 導航項目配置
const navigationItems = [
  {
    title: 'navigation.home',
    path: '/',
    icon: <HomeIcon />,
    primary: true
  },
  {
    title: 'navigation.rfidManagement',
    path: '/rfid',
    icon: <QrCodeIcon />,
    primary: true
  },
  {
    title: 'navigation.uSlump',
    path: '/site-slump',
    icon: <AssessmentIcon />,
    primary: true
  },
  {
    title: 'navigation.contractData',
    path: '/contract-data',
    icon: <DescriptionIcon />,
    primary: true
  },
  {
    title: 'navigation.mixID',
    path: '/mixid',
    icon: <BuildIcon />,
    primary: true
  },
  // {
  //   title: 'navigation.daily',
  //   path: '/daily-report',
  //   icon: <AssignmentIcon />,
  //   primary: true
  // },
  {
    title: 'navigation.first3End3',
    path: '/first3-end3',
    icon: <SearchIcon />,
    primary: true
  },
  {
    title: 'navigation.settings',
    path: '/settings',
    icon: <SettingsIcon />,
    primary: false,
    requiredPermission: 'user.view'
  },

];

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  width?: number;
}

const Sidebar: React.FC<SidebarProps> = ({ open, onToggle, width = 240 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { roleDisplayName } = useRole();

  const handleNavigation = (path: string) => {
    navigate(path);
    // 在移動設備上自動關閉側邊欄
    if (window.innerWidth < 768) {
      onToggle();
    }
  };

  const handleItemClick = (path: string) => {
    handleNavigation(path);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? width : 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : 64,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          zIndex: 1200
        }
      }}
    >
      {/* 側邊欄頭部 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          minHeight: 70,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        {open && (
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Cube Program</Typography>
          // <CastcoLogo size="small"  onClick={() => handleNavigation('/')} />
        )}
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* 導航列表 */}
      <List sx={{ pt: 1 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.title} disablePadding sx={{ display: 'block' }}>
            <Tooltip
              title={!open ? t(item.title) : ''}
              placement="right"
              disableHoverListener={open}
            >
              <ListItemButton
                onClick={() => handleItemClick(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  backgroundColor: isActive(item.path) ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 2 : 'auto',
                    justifyContent: 'center',
                    color: isActive(item.path) ? 'primary.main' : 'inherit'
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={t(item.title)}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive(item.path) ? 600 : 400,
                      color: isActive(item.path) ? 'primary.main' : 'inherit'
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* 底部區域 */}
      <Box sx={{ flexGrow: 1 }} />

      <Divider />

      {/* 用戶信息區域 */}
      <Box sx={{ p: 2 }}>
        {open && user && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
              {user.nickName?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {user.nickName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {roleDisplayName}
              </Typography>
            </Box>
          </Box>
        )}

        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" noWrap>
                Cube Program
                {/* {t('navigation.managementSystem')} */}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                v1.0.0
              </Typography>
            </Box>
          </Box>
        ) }
      </Box>
    </Drawer>
  );
};

export default Sidebar;